import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * /grades/:gradeId/tracks/:trackId — track page. Details header + lessons
 * table (drag-to-reorder, row click → lesson page). 404 if track missing or
 * belongs to a different grade.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Alert, Button, Card, Spin, Tag, Tooltip } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { PlusOutlined } from '@ant-design/icons';
import { deleteLesson, listGrades, listLessons, listTracks, reorderLessons, } from '../../api';
import { PageHeader } from '../../components/PageHeader';
import { notifySuccess } from '../../components/errors';
import { useLoader } from '../../components/useLoader';
import { SortableTable } from '../../components/SortableTable';
import { actionsCol, emptyState, navRowProps } from '../../components/tableHelpers';
import { LessonModal } from '../../components/content/LessonModal';
export default function TrackPageScreen() {
    const nav = useNavigate();
    const { gradeId: gradeIdParam, trackId: trackIdParam } = useParams();
    const gradeId = Number(gradeIdParam);
    const trackId = Number(trackIdParam);
    const [grade, setGrade] = useState(undefined);
    const [track, setTrack] = useState(undefined);
    useEffect(() => {
        (async () => {
            try {
                const grades = await listGrades();
                const g = grades.find((x) => x.id === gradeId) ?? null;
                setGrade(g);
                if (!g) {
                    setTrack(null);
                    return;
                }
                const tracks = await listTracks(gradeId);
                setTrack(tracks.find((t) => t.id === trackId) ?? null);
            }
            catch {
                setGrade(null);
                setTrack(null); /* client auto-surfaces */
            }
        })();
    }, [gradeId, trackId]);
    if (grade === undefined || track === undefined)
        return _jsx(Spin, {});
    if (!grade || !track) {
        return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: "\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", breadcrumb: [{ label: 'ניהול' }, { label: 'כיתות', to: '/grades' }, 'לא נמצא'] }), _jsx(Alert, { type: "warning", message: "\u05D4\u05DE\u05E1\u05DC\u05D5\u05DC \u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0", description: _jsx(_Fragment, { children: _jsx(Link, { to: grade ? `/grades/${grade.id}` : '/grades', children: "\u05D7\u05D6\u05D5\u05E8 \u05DC\u05E8\u05DE\u05D4 \u05E7\u05D5\u05D3\u05DE\u05EA" }) }) })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(PageHeader, { title: track.name, breadcrumb: [
                    { label: 'ניהול' },
                    { label: 'כיתות', to: '/grades' },
                    { label: grade.name, to: `/grades/${grade.id}` },
                    track.name,
                ] }), _jsx(LessonsPanel, { grade: grade, track: track, nav: nav })] }));
}
function LessonsPanel({ grade, track, nav }) {
    const { data, loading, reload } = useLoader(() => listLessons(track.id), [track.id]);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const del = async (l) => {
        try {
            await deleteLesson(l.id);
            notifySuccess('שיעור נמחק');
            reload();
        }
        catch { /* client auto-surfaces */ }
    };
    const cols = [
        {
            title: 'כותרת',
            render: (_, l) => {
                // Content-completeness warning (spec 2.2): a lesson with neither
                // clips nor a passing assessment has no path to "completed", so it
                // silently traps users in the app. Non-blocking (Sam may still be
                // authoring) — just visibly flag it so it can't hide.
                const contentIncomplete = l.clips_count === 0 && l.passing_assessment_id == null;
                return (_jsxs("span", { children: [l.title, contentIncomplete && (_jsx(Tooltip, { title: "\u05E9\u05D9\u05E2\u05D5\u05E8 \u05D6\u05D4 \u05D7\u05E1\u05E8 \u05D2\u05DD \u05E7\u05DC\u05D9\u05E4\u05D9\u05DD \u05D5\u05D2\u05DD \u05D1\u05D5\u05D7\u05DF \u05DE\u05E2\u05D1\u05E8 \u2014 \u05DE\u05E9\u05EA\u05DE\u05E9 \u05E9\u05D9\u05E4\u05EA\u05D7 \u05D0\u05D5\u05EA\u05D5 \u05DC\u05D0 \u05D9\u05D5\u05DB\u05DC \u05DC\u05D4\u05E9\u05DC\u05D9\u05DD \u05D0\u05D5\u05EA\u05D5. \u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05DC\u05E4\u05D7\u05D5\u05EA \u05D0\u05D7\u05D3 \u05DE\u05D4\u05E9\u05E0\u05D9\u05D9\u05DD.", children: _jsx(Tag, { icon: _jsx(WarningOutlined, {}), color: "warning", style: { marginInlineStart: 8 }, children: "\u05E9\u05D9\u05E2\u05D5\u05E8 \u05DC\u05DC\u05D0 \u05D3\u05E8\u05D9\u05E9\u05EA \u05D4\u05E9\u05DC\u05DE\u05D4" }) }))] }));
            },
        },
        {
            title: 'בוחן מעבר', key: '__quiz', width: 130,
            render: (_, l) => l.passing_assessment_id != null
                ? _jsx(Tag, { color: "blue", children: "\u2713 \u05D9\u05E9 \u05D1\u05D5\u05D7\u05DF" })
                : _jsx(Tag, { children: "\u05D0\u05D9\u05DF" }),
        },
        { title: 'קליפים', dataIndex: 'clips_count', width: 80 },
        { title: 'רשות', dataIndex: 'is_optional', width: 60, render: (v) => v ? _jsx(Tag, { children: "\u05E8\u05E9\u05D5\u05EA" }) : null },
        { title: 'חינם', dataIndex: 'is_free', width: 60, render: (v) => v ? _jsx(Tag, { color: "green", children: "\u05D7\u05D9\u05E0\u05DD" }) : null },
        { title: 'חובה לצפות', dataIndex: 'require_clips_watched', width: 100, render: (v) => v ? '✓' : null },
        { title: 'מומלץ', dataIndex: 'recommended_order', width: 70 },
        actionsCol((l) => setEditing(l), del),
    ];
    return (_jsxs(Card, { title: `${data?.length ?? 0} שיעורים`, extra: _jsx(Button, { size: "small", icon: _jsx(PlusOutlined, {}), onClick: () => setCreating(true), children: "\u05E9\u05D9\u05E2\u05D5\u05E8 \u05D7\u05D3\u05E9" }), children: [(!loading && (data?.length ?? 0) === 0)
                ? emptyState('אין עדיין שיעורים — צור את הראשון', { label: 'שיעור חדש', onClick: () => setCreating(true) })
                : (_jsx(SortableTable, { size: "small", loading: loading, dataSource: data ?? [], columns: cols, onRow: navRowProps((l) => nav(`/grades/${grade.id}/tracks/${track.id}/lessons/${l.id}`)), onReorder: async (ids) => { await reorderLessons(track.id, ids); reload(); } })), _jsx(LessonModal, { open: creating, onClose: () => setCreating(false), onDone: () => { setCreating(false); reload(); }, trackId: track.id, gradeId: grade.id }), editing && (_jsx(LessonModal, { open: true, onClose: () => setEditing(null), onDone: () => { setEditing(null); reload(); }, trackId: track.id, gradeId: grade.id, initial: editing }))] }));
}
